import { authenticateTo360, isInteractiveAuthEnabled } from './authService';
import { getEnvironment360URL } from '../configs/appConfig';

// Request queue for handling 401 retries
let isRefreshing = false;
let retryCount = 0;
const MAX_RETRY_COUNT = 3;
const requestQueue: Array<{
    resolve: (value: unknown) => void;
    reject: (error: Error) => void;
    url: string;
    options: RequestInit;
    isText?: boolean;
}> = [];

/**
 * Process queued requests after successful authentication
 */
const processQueue = async (): Promise<void> => {
    while (requestQueue.length > 0) {
        const request = requestQueue.shift();
        if (request) {
            try {
                // Call fetchClient recursively to handle potential 401s from queued requests
                const result = await fetchClient(request.url, request.options, request.isText);
                request.resolve(result);
            } catch (error) {
                request.reject(error instanceof Error ? error : new Error(String(error)));
            }
        }
    }
    retryCount = 0;
};

/**
 * Clear the queue with an error
 */
const clearQueue = (error: Error) => {
    while (requestQueue.length > 0) {
        const request = requestQueue.shift();
        if (request) {
            request.reject(error);
        }
    }
    retryCount = 0;
};

/**
 * Execute the actual fetch request
 */
const executeFetch = async (url: string, options: RequestInit, isText?: boolean): Promise<unknown> => {
    const response = await fetch(url, options);

    if (!response.ok) {
        // Return the response for 401 handling in the main fetchClient
        return response;
    }

    if (isText) {
        return response.text();
    }
    return response.json();
};

/**
 * Enhanced fetch client with interactive authentication support
 */
const fetchClient = async (url: string, options: RequestInit = {}, isText?: boolean): Promise<unknown> => {
    const response = await executeFetch(url, options, isText);

    // Handle 401 for interactive authentication
    if (response instanceof Response && response.status === 401 && isInteractiveAuthEnabled()) {
        // Check if we've exceeded retry limit
        if (retryCount >= MAX_RETRY_COUNT) {
            throw new Error('Maximum authentication retry attempts exceeded');
        }

        // Queue the current request
        return new Promise((resolve, reject) => {
            requestQueue.push({ resolve, reject, url, options, isText });

            // Only trigger auth if not already in progress
            if (!isRefreshing) {
                isRefreshing = true;
                retryCount++;

                const p360Url = getEnvironment360URL();
                
                void authenticateTo360(p360Url, true)
                    .then(() => {
                        isRefreshing = false;
                        void processQueue();
                    })
                    .catch((error) => {
                        isRefreshing = false;
                        clearQueue(error instanceof Error ? error : new Error(String(error)));
                    });
            }
        });
    }

    // Handle non-401 errors
    if (response instanceof Response && !response.ok) {
        throw new Error(`Network response was not ok: ${response.status} ${response.statusText}`);
    }

    return response;
};

export default fetchClient;
